import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { pipe, Option, Array as A } from 'effect';

import { chatAllUsersByIdSelector } from '@/domain/chat';
import { buildTrailExportUrl } from '@/domain/events/exportUrl';
import {
  buildTrailFilterParams,
  TRAIL_FILTERS_INITIAL,
  type TrailFilters,
  type TrailPanelFilters,
} from '@/domain/events/params';
import {
  eventsTrailKeys,
  useEventsTrail,
  useEventsTrailFilters,
  useEventsTrailNewEvents,
} from '@/domain/events/queries';
import type { TrailLink, TrailSource } from '@/domain/events/schemas';
import { showApiErrorToast } from '@/services/api';

import EventsHistoryDrawer from '@/components/mui/Layouts/components/EventsHistoryPanel/components/EventsHistoryDrawer';
import EventsHistoryFiltersBlock from '@/components/mui/Layouts/components/EventsHistoryPanel/components/FiltersBlock';
import EventsHistoryList, {
  type EventsHistoryEntry,
} from '@/components/mui/Layouts/components/EventsHistoryPanel/components/EventsList';

interface EventsTrailPanelProps {
  source: TrailSource;
  isOpened: boolean;
  onClose: () => void;
  dateFormat: string;
  entityId?: string;
  // null — the event is not clickable
  getEventAction?: (link: TrailLink) => (() => void) | null;
  // rendered inside the drawer, so it stacks above it (e.g. a preview)
  children?: React.ReactNode;
}

const HIDDEN_FRAME_TTL_MS = 60_000;

// Blocked-popup fallback: an error response lands in the frame instead of replacing the app.
const downloadInHiddenFrame = (url: string) => {
  const frame = document.createElement('iframe');
  frame.style.display = 'none';
  frame.src = url;
  document.body.appendChild(frame);
  setTimeout(() => frame.remove(), HIDDEN_FRAME_TTL_MS);
};

const loadFailedToast = {
  id: 'eventsHistory.panel.loadFailed',
  defaultMessage: 'Could not load history',
};

const exportFailedToast = {
  id: 'eventsHistory.panel.exportFailed',
  defaultMessage: 'Could not export history',
};

const filtersFailedToast = {
  id: 'eventsHistory.panel.filtersFailed',
  defaultMessage: 'Could not load history filters',
};

const EventsTrailPanel: React.FC<EventsTrailPanelProps> = ({
  source,
  isOpened,
  onClose,
  dateFormat,
  entityId,
  getEventAction,
  children,
}) => {
  const { companyId = '' } = useParams();
  const queryClient = useQueryClient();
  const chatUsersById = useSelector(chatAllUsersByIdSelector);
  const [filters, setFilters] = useState<TrailFilters>(TRAIL_FILTERS_INITIAL);

  const [wasOpened, setWasOpened] = useState(isOpened);
  if (wasOpened !== isOpened) {
    setWasOpened(isOpened);
    if (!isOpened) setFilters(TRAIL_FILTERS_INITIAL);
  }

  // '' — scoped, but the record is not known yet: wait instead of loading the whole company
  const scopeId = entityId || undefined;
  const isScopeReady = entityId === undefined || scopeId !== undefined;
  const canLoad = isOpened && isScopeReady;

  const trail = useEventsTrail(companyId, source, filters, canLoad, scopeId);
  const filterOptions = useEventsTrailFilters(companyId, source, isOpened);

  // A disabled query still returns the cache for its key, and an unready scope shares the company-wide key.
  const pages = isScopeReady ? trail.data?.pages : undefined;
  const eventHash = pages?.[0]?.eventHash ?? null;
  const newEvents = useEventsTrailNewEvents(companyId, source, eventHash, canLoad, scopeId);

  const items = useMemo(() => {
    const flat = (pages ?? []).flatMap((page) =>
      (page.items ?? []).map(({ id, timestamp, userId, username, message, details, link }) => {
        const action = link && getEventAction ? getEventAction(link) : null;
        return { id, timestamp, userId, username, message, details, link: action ? link : null, action };
      }),
    );
    return Array.from(new Map(flat.map((entry) => [entry.id, entry])).values());
  }, [pages, getEventAction]);

  const handleEventAction = useCallback((entry: EventsHistoryEntry) => entry.action?.(), []);

  const picturesByUser = useMemo(
    () => new Map((filterOptions.data?.users ?? []).map((user) => [user.value, user.picture ?? undefined])),
    [filterOptions.data],
  );

  const pictureOf = useCallback(
    (key: string): string | undefined =>
      picturesByUser.get(key) ?? (chatUsersById.getIn([key, 'picture']) as string | undefined),
    [picturesByUser, chatUsersById],
  );

  const getAvatarSrc = useCallback(
    (entry: EventsHistoryEntry) =>
      pipe(
        [entry.username, entry.userId],
        A.filterMap(Option.fromNullable),
        A.filterMap((key) => Option.fromNullable(pictureOf(key))),
        A.head,
        Option.getOrUndefined,
      ),
    [pictureOf],
  );

  useEffect(() => {
    if (trail.error) showApiErrorToast(trail.error, loadFailedToast);
  }, [trail.error]);

  useEffect(() => {
    if (filterOptions.error) showApiErrorToast(filterOptions.error, filtersFailedToast);
  }, [filterOptions.error]);

  const handleChangeFilters = useCallback((panelFilters: TrailPanelFilters) => {
    setFilters((prev) => ({ ...prev, ...panelFilters }));
  }, []);

  const handleChangeDate = useCallback((from: Date | null, to: Date | null) => {
    setFilters((prev) => ({ ...prev, from, to }));
  }, []);

  const handleGetNextPage = useCallback(() => {
    if (trail.hasNextPage && !trail.isFetchingNextPage) trail.fetchNextPage();
  }, [trail]);

  const handleReload = useCallback(() => {
    queryClient.resetQueries({
      queryKey: eventsTrailKeys.list(companyId, source, scopeId, buildTrailFilterParams(filters)),
    });
    queryClient.invalidateQueries({
      queryKey: eventsTrailKeys.newEvents(companyId, source, scopeId, eventHash),
    });
  }, [queryClient, companyId, source, scopeId, filters, eventHash]);

  const handleExport = useCallback(async () => {
    try {
      const url = await buildTrailExportUrl(source, filters, scopeId);
      // Opened straight at the URL: the browser closes a tab whose first navigation is a download.
      if (!window.open(url, '_blank')) downloadInHiddenFrame(url);
    } catch (err) {
      showApiErrorToast(err, exportFailedToast);
    }
  }, [source, filters, scopeId]);

  const isNext = trail.isLoading || !isScopeReady ? undefined : (trail.hasNextPage && 'next') || null;

  return (
    <EventsHistoryDrawer toggleOpen={onClose} isOpened={isOpened}>
      <EventsHistoryFiltersBlock
        users={filterOptions.data?.users ?? []}
        types={filterOptions.data?.types ?? []}
        isBusy={filterOptions.isLoading}
        onChangeFilters={handleChangeFilters}
        onChangeDate={handleChangeDate}
        onClickExport={handleExport}
        dateFormat={dateFormat}
        isDisabledExport={items.length === 0}
      />

      <EventsHistoryList
        items={items}
        getAvatarSrc={getAvatarSrc}
        onAction={handleEventAction}
        getNextEvents={handleGetNextPage}
        isNext={isNext}
        onReload={handleReload}
        counter={newEvents.data?.count ?? 0}
        dateFormat={dateFormat}
        isError={trail.isError}
      />
      {children}
    </EventsHistoryDrawer>
  );
};

export default EventsTrailPanel;
